import React, {
    createContext,
    useContext,
    useState,
    useCallback,
    useEffect,
    useRef
} from "react";
import { useGetUserPermission } from "../services/user.service";
import { normalizePermissions } from "../utils/normalizePermissions";
import { storage } from "../utils/storage";
import {  message } from "antd";
type PermissionMap = {
    [moduleKey: string]: {
        [action: string]: boolean;
    };
};

type AuthContextType = {
    permissions: PermissionMap | null;
    permissionsLoaded: boolean;
    loadPermissions: () => Promise<PermissionMap>;
    hasPermission: (module: string, action: string) => boolean;
    logout: () => void;
};

const AuthContext = createContext<AuthContextType | null>(null);

export const getToken = () => storage.getToken();

export const isTokenExpired = (token: string | null) => {
    if (!token) return true;
    try {
        const payload = JSON.parse(atob(token.split(".")[1]));
        return Date.now() >= payload.exp * 1000;
    } catch {
        return true;
    }
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
    const [permissions, setPermissions] = useState<PermissionMap | null>(null);
    const [permissionsLoaded, setPermissionsLoaded] = useState(false);

    const permissionMutation = useGetUserPermission({
        onError: (error: any) => {
            message.error(error?.response?.statusMessage);
        }
    });

    const mutationRef = useRef(permissionMutation);
    useEffect(() => {
        mutationRef.current = permissionMutation;
    });

    const logout = useCallback(() => {
        storage.clearToken();
        storage.clearRefreshToken();
        storage.clearEmailId();
        storage.clearPhoneNumber();
        storage.clearCodeType();
        storage.clearDialingCode();
        storage.clearCountryId();
        storage.clearUserRoleId();

        if (window.location.pathname !== "/") {
            window.location.href = "/";
        }
    }, []);

    const loadPermissions = useCallback(async (): Promise<PermissionMap> => {
        const token = storage.getToken();

        if (!token || isTokenExpired(token)) {
            logout();
            return {};
        }

        try {
            const res = await mutationRef.current.mutateAsync({});
            const permissionArray = res?.data ?? [];
            const normalized = normalizePermissions(permissionArray);

            setPermissions(normalized);
            setPermissionsLoaded(true);

            return normalized;
        } catch (error) {
            console.error("Permission load error", error);
            setPermissions(null);
            setPermissionsLoaded(false);
            return {};
        }
    }, [logout]);

    useEffect(() => {
        const token = storage.getToken();

        if (!token || isTokenExpired(token)) {
            logout();
            return;
        }

        loadPermissions();
    }, []);

    const hasPermission = useCallback(
        (module: string, action: string) => {
            if (!permissions) return false;
            return Boolean(permissions[module]?.[action.toLowerCase()]);
        },
        [permissions]
    );

    return (
        <AuthContext.Provider
            value={{
                permissions,
                permissionsLoaded,
                loadPermissions,
                hasPermission,
                logout,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const ctx = useContext(AuthContext);
    if (!ctx) {
        throw new Error("useAuth must be used inside AuthProvider");
    }
    return ctx;
};